import React from 'react';
import { charsets, PasswordPolicy } from 'password-sheriff';
import { formatMessage } from 'translate';
import { change } from 'redux-form';
import PolicyMessages from './constants/messages';
import { fetchAPI } from '../../../_core/http/fetch';
import { authHelpers } from '../../../Auth/helpers';
import { emailRegex } from '../../../constants/regex';

export const AUTH_FIELDS = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  password: 'password',
  handle: 'handle',
  remember_me: 'remember_me',
  agree_with_terms: 'agree_with_terms',
  emailPasswordLess: 'emailPasswordLess',
  // new signup/login funnel
  handleValidated: 'handleValidated',
  step: 'step',
  subscribe: 'subscribe',
};

export const AUTH_SIGNUP_STEPS = {
  meta: 'meta',
  handle: 'handle',
  checkMail: 'checkMail',
};

const containsPolicy = new PasswordPolicy({
  containsAtLeast: {
    atLeast: 3,
    expressions: [charsets.lowerCase, charsets.upperCase, charsets.numbers, charsets.specialCharacters],
  },
});

const lengthPolicy = new PasswordPolicy({
  length: { minLength: 8, maxLength: 21 },
});

export const normalizeTextField = value => value && value.replace(/[^\w-]+/g, '');

export const normalizeFullName = value => value && value.replace(/[^\w-.' ']+/g, '');

export const validatePasswordLessSignIn = values => {
  const errors = {};
  [AUTH_FIELDS.emailPasswordLess].forEach(field => {
    if (!values[field]) {
      errors[field] = formatMessage({ id: 'main.required' });
    }
  });
  if (!emailRegex.test(values[AUTH_FIELDS.emailPasswordLess])) {
    errors[AUTH_FIELDS.emailPasswordLess] = formatMessage({ id: 'auth.wrong_email' });
  }
  return errors;
};

export const validateEmailSignIn = values => {
  const errors = {};
  const { password, email } = AUTH_FIELDS;
  [password, email].forEach(field => {
    if (!values[field]) {
      errors[field] = formatMessage({ id: 'main.required' });
    }
  });
  if (values[email] && !emailRegex.test(values[email])) {
    errors[email] = formatMessage({ id: 'auth.wrong_email' });
  }
  if (values[password] && values[password].length > 21) {
    errors[password] = formatMessage({ id: 'auth.password_length' }, { d: 8, n: 21 });
  }
  return errors;
};

export const validateEmailSignUpMeta = values => {
  const errors = {};
  const { firstName, lastName, email } = AUTH_FIELDS;

  [firstName, lastName, email].forEach(field => {
    if (!values[field]) {
      errors[field] = formatMessage({ id: 'main.required' });
    }
  });
  if (values[email] && !emailRegex.test(values[email])) {
    errors[email] = formatMessage({ id: 'auth.wrong_email' });
  }
  if ((values[firstName] || []).length > 35) {
    errors[firstName] = formatMessage({ id: 'auth.first_name_length' });
  }
  if ((values[lastName] || []).length > 35) {
    errors[lastName] = formatMessage({ id: 'auth.last_name_length' });
  }
  return errors;
};

export const validateEmailSignUpAll = values => {
  const errors = {};
  const {
    firstName, lastName, email, password, handle,
  } = AUTH_FIELDS;
  [firstName, lastName, email, password, handle].forEach(field => {
    if (!values[field]) {
      errors[field] = formatMessage({ id: 'main.required' });
    }
  });
  if (values[email] && !emailRegex.test(values.email)) {
    errors[email] = formatMessage({ id: 'auth.wrong_email' });
  }
  if (values[password] && !lengthPolicy.check(values[password])) {
    const [{ code, format }] = lengthPolicy.explain(values.password);
    errors[password] = formatMessage(PolicyMessages[code], { d: format[0] });
  } else if (!containsPolicy.check(values[password])) {
    const [{
      code, format, items,
    }] = containsPolicy.explain(values[password]);
    const policies = items.map(item => formatMessage(PolicyMessages[item.code]));
    errors[password] = (
      <React.Fragment>
        {formatMessage(PolicyMessages[code], { d: format[0], n: format[1] })}<br />
        <ul>{policies.map(policy => (<li key={policy}>{policy}</li>))}</ul>
      </React.Fragment>
    );
  }
  if (values[password] && values[password].length > 21) {
    errors[password] = formatMessage({ id: 'auth.password_length' }, { d: 8, n: 21 });
  }
  return errors;
};

export const validateSignUpEamil = values => (values.step === 'meta'
  ? validateEmailSignUpMeta(values) : validateEmailSignUpAll(values));

export const valdateSignUpHandleOnly = values => {
  const errors = {};
  [AUTH_FIELDS.handle].forEach(field => {
    if (!values[field]) {
      errors[field] = formatMessage({ id: 'main.required' });
    }
  });
  return errors;
};

export const validateEmailSignUpHandle = (values, dispatch) => {
  const error = new Error('handle validation error');
  const { handle, handleValidated, step } = AUTH_FIELDS;
  error[handle] = formatMessage({ id: 'auth.handle_taken' });
  if (!values[handle] || values[step] === 'meta' || values[handleValidated] === values[handle]) {
    return Promise.resolve(error);
  }
  if (values[handle].length < 4) {
    error[handle] = formatMessage({ id: 'form_validation.min_length' }, { min: 4 });
    return Promise.reject(error);
  }
  if (!authHelpers.isAuthenticated()) {
    authHelpers.generateGuestToken();
  }
  return fetchAPI(`handle?handle=${encodeURIComponent(values[handle])}`)
    .then(({ handle_exists }) => {
      if (handle_exists) {
        throw error;
      }
      dispatch(change('login', AUTH_FIELDS.handleValidated, values[handle]));
    }).catch(() => {
      throw error;
    });
};
